import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, TrendingUp, Users, Star } from 'lucide-react';
import api from '../lib/api';

// Get color based on score
const getScoreColor = (score) => {
  if (score >= 90) return 'text-green-600';
  if (score >= 80) return 'text-blue-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 60) return 'text-orange-600';
  return 'text-red-600';
};

const getGradeColor = (grade) => {
  const colors = {
    'A': 'bg-green-100 text-green-700',
    'B': 'bg-blue-100 text-blue-700',
    'C': 'bg-yellow-100 text-yellow-700',
    'D': 'bg-orange-100 text-orange-700',
    'F': 'bg-red-100 text-red-700'
  };
  return colors[grade] || 'bg-gray-100 text-gray-700';
};

export default function QualityScoreWidget() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.getQualityStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch quality stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="corp-card animate-pulse">
        <div className="h-1 w-full bg-gray-200" />
        <div className="p-6">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="h-20 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!stats || stats.total_reviewed === 0) {
    return (
      <div className="corp-card" data-testid="quality-score-widget">
        <div className="h-1 w-full bg-purple-500" />
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-purple-600" />
            <h3 className="font-bold text-slate-800">AI Quality Scores</h3>
          </div>
          <p className="text-sm text-gray-500 text-center py-6">
            No tickets reviewed yet. Scores will appear when tickets are closed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="corp-card" data-testid="quality-score-widget">
      <div className="h-1 w-full bg-purple-500" />
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-purple-600" />
            <h3 className="font-bold text-slate-800">AI Quality Scores</h3>
          </div>
          <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded">
            {stats.total_reviewed} reviewed
          </span>
        </div>

        {/* Main Score */}
        <div className="flex items-center justify-center mb-6">
          <div className="text-center">
            <div className={`text-5xl font-bold ${getScoreColor(stats.avg_score)}`}>
              {stats.avg_score}
            </div>
            <div className="text-sm text-gray-500 mt-1">Average Score</div>
          </div>
        </div>

        {/* Grade Distribution */}
        <div className="mb-5">
          <div className="text-xs font-semibold text-gray-500 mb-2">GRADE DISTRIBUTION (Click to view tickets)</div>
          <div className="flex gap-1">
            {['A', 'B', 'C', 'D', 'F'].map(grade => {
              const count = stats.grade_distribution?.[grade] || 0;
              const pct = stats.total_reviewed > 0 ? (count / stats.total_reviewed * 100) : 0;
              return (
                <div 
                  key={grade} 
                  className={`flex-1 text-center ${count > 0 ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                  onClick={() => count > 0 && navigate(`/tickets?grade=${grade}`)}
                  title={count > 0 ? `View ${count} tickets with grade ${grade}` : ''}
                >
                  <div 
                    className="h-12 rounded-t flex items-end justify-center"
                    style={{ 
                      background: `linear-gradient(to top, ${
                        grade === 'A' ? '#dcfce7' : 
                        grade === 'B' ? '#dbeafe' : 
                        grade === 'C' ? '#fef9c3' : 
                        grade === 'D' ? '#fed7aa' : '#fecaca'
                      } ${pct}%, #f8fafc ${pct}%)`
                    }}
                  >
                    {count > 0 && (
                      <span className="text-xs font-semibold mb-1">{count}</span>
                    )}
                  </div>
                  <div className={`text-xs font-bold py-1 rounded-b ${getGradeColor(grade)}`}>
                    {grade}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Performers */}
        {stats.by_assignee && stats.by_assignee.length > 0 && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 mb-2">
              <Users className="h-3 w-3" />
              TOP PERFORMERS
            </div>
            <div className="space-y-2">
              {stats.by_assignee.slice(0, 3).map((person, idx) => (
                <div key={person.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {idx === 0 && <Star className="h-3 w-3 text-yellow-500" />}
                    <span className="text-sm font-medium text-gray-700">{person.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${getScoreColor(person.avg_score)}`}>
                      {person.avg_score}
                    </span>
                    <span className="text-xs text-gray-400">({person.tickets})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Reviews */}
        {stats.recent_reviews && stats.recent_reviews.length > 0 && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 mb-2">
              <TrendingUp className="h-3 w-3" />
              RECENT SCORES (Click to view)
            </div>
            <div className="space-y-1">
              {stats.recent_reviews.slice(0, 5).map((review, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                  onClick={() => navigate(`/tickets/${review.ticket_id || ''}`)}
                  data-testid={`review-link-${review.ticket_number}`}
                >
                  <span className="text-gray-600 truncate max-w-[150px]">
                    #{review.ticket_number} {review.summary}
                  </span>
                  <span className={`font-bold ${getScoreColor(review.score)}`}>
                    {review.score}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
